import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Loader2, Tag, User } from "lucide-react";
import { blogApi } from "../api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReactMarkdown from "react-markdown";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";

const SITE_BASE = "https://www.peptidesformedspas.org";
const SITE_IMAGE = `${SITE_BASE}/og-image.jpg`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageSEO(
    post ? post.title : undefined,
    post ? post.excerpt : undefined
  );

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blogApi.getBySlug(slug);
      setPost(data);
      setError(null);
    } catch (err) {
      console.error(`[BlogPostPage] Failed to load post '${slug}':`, err);
      setError("Article not found");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fffaf5]">
        <Header />
        <div className="flex justify-center items-center py-40">
          <Loader2 className="w-8 h-8 text-[#b89a6d] animate-spin" />
          <span className="ml-3 text-[#7a6b62]">Loading article...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#fffaf5]">
        <Header />
        <div className="flex flex-col justify-center items-center py-40">
          <h1 className="text-2xl text-[#3a2a26] mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#b89a6d] hover:text-[#a08560]">
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const postUrl = `${SITE_BASE}/blog/${slug}`;
  const publishedIso = post.published_at || post.publishedAt || post.createdAt || post.date;
  const modifiedIso = post.updated_at || post.updatedAt || publishedIso;
  const blogJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${postUrl}#article`,
      headline: post.title,
      description: post.excerpt,
      image: SITE_IMAGE,
      url: postUrl,
      datePublished: publishedIso,
      dateModified: modifiedIso,
      author: { "@type": "Organization", name: post.author || "PeptidesForMedSpas Editorial", url: SITE_BASE },
      publisher: { "@id": `${SITE_BASE}/#organization` },
      mainEntityOfPage: { "@type": "WebPage", "@id": postUrl },
      articleSection: post.category || "Education",
      inLanguage: "en-US",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${postUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_BASE}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_BASE}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: postUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      <JsonLd id={`blog-post-jsonld-${slug}`} data={blogJsonLd} />
      
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-[#b89a6d] hover:text-[#a08560] mb-6 group"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Link>
          
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-[#f7e8e4] text-[#b89a6d] text-sm font-medium rounded-full">
              {post.category}
            </span>
            <span className="flex items-center text-[#a09186] text-sm">
              <Clock className="w-4 h-4 mr-1" />
              {post.readTime}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#3a2a26] mb-6" data-testid="blog-post-title">
            {post.title}
          </h1>
          
          <p className="text-xl text-[#7a6b62] mb-6">
            {post.excerpt}
          </p>
          
          <div className="flex flex-wrap items-center gap-6 text-[#a09186]">
            <span className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              {post.author}
            </span>
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {formatDate(post.created_at)}
            </span>
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article 
            className="prose prose-invert prose-cyan max-w-none
              prose-headings:text-[#3a2a26] prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-[#5a4a40] prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-[#b89a6d] prose-a:no-underline hover:prose-a:text-[#a08560]
              prose-strong:text-[#3a2a26]
              prose-ul:text-[#5a4a40] prose-ol:text-[#5a4a40]
              prose-li:mb-2
              prose-table:border prose-table:border-[#f0d8d4]
              prose-th:bg-white prose-th:text-[#3a2a26] prose-th:p-3 prose-th:border prose-th:border-[#f0d8d4]
              prose-td:p-3 prose-td:border prose-td:border-[#f0d8d4] prose-td:text-[#5a4a40]
              prose-blockquote:border-[#d4a5ad] prose-blockquote:bg-white prose-blockquote:p-4 prose-blockquote:rounded-lg
            "
            data-testid="blog-post-content"
          >
            {/* Strip HTML comments — authoring markers like idempotence guards
                would otherwise render as visible text (react-markdown escapes
                unknown HTML by default). */}
            <ReactMarkdown>{(post.content || "").replace(/<!--[\s\S]*?-->/g, "")}</ReactMarkdown>
          </article>
          
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-[#f0d8d4]">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-[#a09186]" />
                {post.tags.map((tag) => (
                  <span 
                    key={tag}
                    className="px-3 py-1 bg-white text-[#7a6b62] text-sm rounded-full border border-[#f0d8d4]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <h3 className="text-[#b89a6d] font-bold text-lg mb-3">Medical Disclaimer</h3>
            <p className="text-[#7a6b62] text-sm leading-relaxed">
              This article is for educational purposes only and does not constitute medical advice. 
              The information provided should not be used for diagnosing or treating any health condition. 
              Always consult with a qualified healthcare provider before making decisions about your health 
              or before starting any new treatment or supplement regimen.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
